/**
 * Utility to load images from S3 by trying sequential numbered filenames
 * until a 404 is encountered
 */

export interface GalleryImage {
    url: string;
    index: number;
}

/**
 * Load images from S3 with sequential numbering (01.jpg, 02.jpg, etc.)
 * Stops when it encounters a 404
 * Uses Image() constructor to avoid CORS preflight issues
 * Loads all images in parallel for faster loading
 */
export async function loadGalleryImages(
    baseUrl: string,
    extension: string = 'jpg',
    maxImages: number = 100
): Promise<GalleryImage[]> {
    // Create array of all possible image URLs to check
    const checkPromises = Array.from({ length: maxImages }, (_, i) => {
        const index = i + 1;
        const paddedIndex = index.toString().padStart(2, '0');
        const url = `${baseUrl}${paddedIndex}.${extension}`;

        return new Promise<GalleryImage | null>((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ url, index });
            img.onerror = () => resolve(null);
            img.src = url;
        });
    });

    // Load all images in parallel
    const results = await Promise.all(checkPromises);

    // Filter out nulls and find the last successful image
    const validImages = results.filter((img): img is GalleryImage => img !== null);

    // Find the first gap (3 consecutive failures)
    let lastValidIndex = 0;
    let consecutiveFailures = 0;

    for (let i = 1; i <= maxImages; i++) {
        const hasImage = validImages.some(img => img.index === i);
        if (hasImage) {
            lastValidIndex = i;
            consecutiveFailures = 0;
        } else {
            consecutiveFailures++;
            if (consecutiveFailures >= 3) {
                break;
            }
        }
    }

    // Return only images up to the last valid index
    return validImages
        .filter(img => img.index <= lastValidIndex)
        .sort((a, b) => a.index - b.index);
}
